import { apiHelper } from './api';
import { ALLOWED_FILE_TYPES } from '@utils/constants';

// Maximum file size in bytes (10MB)
const MAX_FILE_SIZE = parseInt(import.meta.env.VITE_MAX_FILE_SIZE) || 10 * 1024 * 1024;

// Allowed file extensions
const ALLOWED_EXTENSIONS = (import.meta.env.VITE_ALLOWED_FILE_TYPES || 'jpg,jpeg,png,pdf,doc,docx,xls,xlsx,txt,csv')
  .split(',')
  .map(ext => ext.trim().toLowerCase());

/**
 * Validate file before upload
 * @param {File} file - File to validate
 * @returns {Object} - Validation result
 */
export const validateFile = (file) => {
  const errors = [];

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    errors.push(`File size exceeds ${formatFileSize(MAX_FILE_SIZE)} limit`);
  }

  // Check file type
  const fileExtension = file.name.split('.').pop()?.toLowerCase();
  const isValidType = ALLOWED_FILE_TYPES.ALL.includes(file.type) ||
        ALLOWED_EXTENSIONS.includes(fileExtension);

  if (!isValidType) {
    errors.push(`File type '${file.type}' is not supported`);
  }

  // Check file name
  if (!file.name || file.name.trim() === '') {
    errors.push('File name is required');
  }

  // Check for potentially dangerous file names
  const dangerousPatterns = [
    /\.(exe|bat|cmd|scr|pif|vbs|js|jar|com|msi)$/i,
    /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(\..*)?$/i
  ];

  if (dangerousPatterns.some(pattern => pattern.test(file.name))) {
    errors.push('File name contains potentially dangerous content');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Format file size in human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get file icon based on file type
 * @param {string} fileName - Name of the file
 * @param {string} mimeType - MIME type of the file
 * @returns {string} - Icon name for lucide-react
 */
export const getFileIcon = (fileName, mimeType) => {
  const extension = fileName.split('.').pop()?.toLowerCase();

  // Image files
  if (mimeType?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
    return 'Image';
  }

  // Document files
  if (mimeType === 'application/pdf' || extension === 'pdf') {
    return 'FileText';
  }

  // Word documents
  if (mimeType?.includes('word') || ['doc', 'docx'].includes(extension)) {
    return 'FileText';
  }

  // Excel files
  if (mimeType?.includes('sheet') || ['xls', 'xlsx', 'csv'].includes(extension)) {
    return 'FileSpreadsheet';
  }

  // Text files
  if (mimeType?.startsWith('text/') || ['txt', 'md', 'json'].includes(extension)) {
    return 'FileText';
  }

  // Archive files
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
    return 'Archive';
  }

  // Default file icon
  return 'File';
};

/**
 * Generate unique filename to prevent conflicts
 * @param {string} originalName - Original filename
 * @returns {string} - Unique filename
 */
export const generateUniqueFileName = (originalName) => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 8);
  const extension = originalName.split('.').pop();
  const nameWithoutExt = originalName.substring(0, originalName.lastIndexOf('.'));

  return `${nameWithoutExt}_${timestamp}_${random}.${extension}`;
};

/**
 * Upload single file
 * @param {File} file - File to upload
 * @param {Object} options - Upload options
 * @returns {Promise} - Upload promise
 */
export const uploadSingleFile = async (file, options = {}) => {
  // Validate file
  const validation = validateFile(file);
  if (!validation.isValid) {
    throw new Error(validation.errors.join(', '));
  }

  const formData = new FormData();
  formData.append('file', file);

  // Add metadata if provided
  if (options.description) {
    formData.append('description', options.description);
  }

  if (options.category) {
    formData.append('category', options.category);
  }

  const config = {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    timeout: 60000, // 60 seconds timeout for uploads
    ...options.config
  };

  // Add upload progress callback if provided
  if (options.onProgress) {
    config.onUploadProgress = (progressEvent) => {
      const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      options.onProgress(progress);
    };
  }

  const response = await apiHelper.post('/upload', formData, config);
  return response.data;
};

/**
 * Upload multiple files
 * @param {File[]} files - Array of files to upload
 * @param {Object} options - Upload options
 * @returns {Promise} - Upload promises array
 */
export const uploadMultipleFiles = async (files, options = {}) => {
  // Validate all files first
  const validationResults = files.map(file => ({
    file,
    validation: validateFile(file)
  }));

  const invalidFiles = validationResults.filter(result => !result.validation.isValid);
  if (invalidFiles.length > 0) {
    const errors = invalidFiles.map(result =>
      `${result.file.name}: ${result.validation.errors.join(', ')}`
    );
    throw new Error(`Invalid files:\n${errors.join('\n')}`);
  }

  // Upload files concurrently or sequentially based on options
  if (options.sequential) {
    const results = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const result = await uploadSingleFile(file, {
          ...options,
          onProgress: options.onProgress ?
            (progress) => options.onProgress(i, progress, file.name) :
            undefined
        });
        results.push({ file: file.name, success: true, data: result });
      } catch (error) {
        results.push({
          file: file.name,
          success: false,
          error: error.message
        });

        // Stop on first error if specified
        if (options.stopOnError) {
          throw new Error(`Upload failed for ${file.name}: ${error.message}`);
        }
      }
    }
    return results;
  } else {
    // Concurrent upload with Promise.allSettled
    const uploadPromises = files.map((file, index) =>
      uploadSingleFile(file, {
        ...options,
        onProgress: options.onProgress ?
          (progress) => options.onProgress(index, progress, file.name) :
          undefined
      }).then(result => ({
        file: file.name,
        success: true,
        data: result
      })).catch(error => ({
        file: file.name,
        success: false,
        error: error.message
      }))
    );

    return await Promise.allSettled(uploadPromises);
  }
};

/**
 * Upload file to specific endpoint (e.g., ticket attachments)
 * @param {string} endpoint - API endpoint
 * @param {File|File[]} files - File(s) to upload
 * @param {Object} options - Upload options
 * @returns {Promise} - Upload promise
 */
export const uploadToEndpoint = async (endpoint, files, options = {}) => {
  const fileArray = Array.isArray(files) ? files : [files];

  // Validate files
  const validationResults = fileArray.map(file => validateFile(file));
  const invalidFiles = validationResults.filter(result => !result.isValid);

  if (invalidFiles.length > 0) {
    const errors = invalidFiles.flatMap(result => result.errors);
    throw new Error(errors.join(', '));
  }

  const formData = new FormData();
  fileArray.forEach(file => {
    formData.append('files', file);
  });

  // Add additional form data
  if (options.data) {
    Object.entries(options.data).forEach(([key, value]) => {
      formData.append(key, value);
    });
  }

  const config = {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    timeout: 120000, // 2 minutes timeout for multiple files
    ...options.config
  };

  if (options.onProgress) {
    config.onUploadProgress = (progressEvent) => {
      const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      options.onProgress(progress);
    };
  }

  const response = await apiHelper.post(endpoint, formData, config);
  return response.data;
};

/**
 * Delete uploaded file
 * @param {string} fileId - ID of the file to delete
 * @param {string} endpoint - Delete endpoint (optional)
 * @returns {Promise} - Delete promise
 */
export const deleteFile = async (fileId, endpoint = '/upload') => {
  const response = await apiHelper.delete(`${endpoint}/${fileId}`);
  return response.data;
};

/**
 * Download file
 * @param {string} fileId - ID of the file to download
 * @param {string} filename - Original filename
 * @param {string} endpoint - Download endpoint (optional)
 * @returns {Promise} - Download promise
 */
export const downloadFile = async (fileId, filename, endpoint = '/upload') => {
  const response = await apiHelper.get(`${endpoint}/${fileId}/download`, {
    responseType: 'blob'
  });

  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);

  return response;
};

/**
 * Preview file (for images and PDFs)
 * @param {string} fileId - ID of the file to preview
 * @param {string} endpoint - Preview endpoint (optional)
 * @returns {Promise<string>} - Preview URL
 */
export const getPreviewUrl = async (fileId, endpoint = '/upload') => {
  const response = await apiHelper.get(`${endpoint}/${fileId}/preview`);
  return response.data.url;
};

/**
 * Get file metadata
 * @param {string} fileId - ID of the file
 * @param {string} endpoint - Metadata endpoint (optional)
 * @returns {Promise} - File metadata
 */
export const getFileMetadata = async (fileId, endpoint = '/upload') => {
  const response = await apiHelper.get(`${endpoint}/${fileId}/metadata`);
  return response.data;
};

/**
 * Compress image before upload
 * @param {File} file - Image file to compress
 * @param {Object} options - Compression options
 * @returns {Promise<File>} - Compressed file
 */
export const compressImage = (file, options = {}) => {
  return new Promise((resolve, reject) => {
    const {
      maxWidth = 1920,
      maxHeight = 1080,
      quality = 0.8,
      format = file.type
    } = options;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;

      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width *= ratio;
        height *= ratio;
      }

      canvas.width = width;
      canvas.height = height;

      // Draw compressed image
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob((blob) => {
        if (blob) {
          const compressedFile = new File([blob], file.name, {
            type: format,
            lastModified: Date.now()
          });
          resolve(compressedFile);
        } else {
          reject(new Error('Failed to compress image'));
        }
      }, format, quality);
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};

export default {
  validateFile,
  formatFileSize,
  getFileIcon,
  generateUniqueFileName,
  uploadSingleFile,
  uploadMultipleFiles,
  uploadToEndpoint,
  deleteFile,
  downloadFile,
  getPreviewUrl,
  getFileMetadata,
  compressImage
};